// Groceries (redesign slice 6b; wireframes 6, 15, 16, 25;
// docs/2026-10-04-groceries-design.md). The list for one week, saved and
// shared live with the household (useGroceryState, slice 6a):
//
// - its own week switcher, always opening on the current week (Q6);
// - two modes: Shop (tap a row: in the cart) and Check the kitchen (tap a
//   row: already have, Q10);
// - what's left to buy by aisle, then added items with Pin / Pinned (Q7),
//   then the folded "Already have", "In the cart" and "Not buying" groups;
// - each line says how many recipes need it, and opens to say which and
//   when, with "Not buying this week";
// - done and nothing-planned states; Print prints what is left to buy.
import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMealPlanner } from "../hooks/MealPlannerContext";
import { useGroceryState } from "../hooks/useGroceryState";
import { useIsMobile } from "../hooks/useIsMobile";
import { PrintPortal } from "../components/PrintPortal";
import { COLORS, FONT_SIZE, LAYOUT, RADIUS, SPACE } from "../lib/constants";
import { capitalizeFirst, currentWeekStart, formatQuantity, shiftWeek } from "../lib/utils";
import { prettyFractions } from "../lib/recipeDisplay";
import { weekRange } from "../lib/planView";
import { amountText, groceryView, groceryWeekLabel, lineSources, rowKey, rowName, weekLines, type Row } from "../lib/groceryView";
import { extrasForWeek, progress, type Mark } from "../lib/groceryState";
import { smallLabel, supporting } from "../components/recipeStyles";
import { inputStyle, roundButton, textButton } from "../components/plan/planStyles";

type Mode = "shop" | "kitchen";
type Group = "have" | "cart" | "removed";

const qty = (q: number) => prettyFractions(String(formatQuantity(q)));
const rowAmount = (row: Row) => (row.kind === "line" ? amountText(row.line.amounts, qty) : row.extra.amount);

export function GroceryPage() {
  const mp = useMealPlanner();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const thisWeek = currentWeekStart(mp.planDefaults.weekStartDay);
  const [week, setWeek] = useState(thisWeek);
  const [mode, setMode] = useState<Mode>("shop");
  const [open, setOpen] = useState<Record<Group, boolean>>({ have: false, cart: false, removed: false });
  const [detail, setDetail] = useState<string | null>(null);
  const grocery = useGroceryState(mp.householdId, week, mp.reloadWeekPlans);

  const { lines, placements } = useMemo(() => weekLines(mp.plansByWeek, week, mp.recipeById), [mp.plansByWeek, week, mp.recipeById]);
  const view = useMemo(() => groceryView(lines, grocery.extras, week, grocery.marks), [lines, grocery.extras, week, grocery.marks]);
  const counts = progress(lines, extrasForWeek(grocery.extras, week), grocery.marks);
  const done = counts.total > 0 && counts.left === 0;
  const boughtCount = view.cart.length;
  const haveCount = view.have.length;

  const setRowMark = (row: Row, mark: Mark | null) => grocery.setMark(rowKey(row), mark, row.kind === "line" ? row.line.amounts : []);
  const tapMark: Mark = mode === "shop" ? "cart" : "have";

  const sync = !grocery.online
    ? grocery.pending > 0
      ? "Offline: your checks will save when you're back online"
      : "Offline"
    : grocery.pending > 0
      ? grocery.saveFailed
        ? "Couldn't save your checks yet; trying again"
        : "Saving…"
      : "Shared with your household · up to date";

  const goToWeek = (w: string) => {
    setWeek(w);
    setDetail(null);
  };

  const rowList = (rows: Row[], checked: boolean) =>
    rows.map((row) => (
      <GroceryRow
        key={rowKey(row)}
        row={row}
        checked={checked}
        open={detail === rowKey(row)}
        onToggle={() => setRowMark(row, checked ? null : tapMark)}
        onDetail={() => setDetail((d) => (d === rowKey(row) ? null : rowKey(row)))}
        onRemove={() => {
          setRowMark(row, "removed");
          setDetail(null);
        }}
        onPin={(pinned) => row.kind === "extra" && grocery.setPinned(row.extra, pinned).catch(() => undefined)}
        onDelete={() => row.kind === "extra" && grocery.removeExtra(row.extra).catch(() => undefined)}
        sources={row.kind === "line" && detail === rowKey(row) ? lineSources(row.line, placements, mp.recipeById) : []}
      />
    ));

  return (
    <div className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.x3, maxWidth: isMobile ? undefined : 960 }}>
      <header style={{ display: "flex", flexDirection: "column", gap: SPACE.lg, maxWidth: 560 }}>
        <div style={{ display: "flex", alignItems: "center", gap: SPACE.md }}>
          <button type="button" aria-label="Previous week" onClick={() => goToWeek(shiftWeek(week, -1))} style={roundButton}>
            ‹
          </button>
          <div style={{ flex: 1, textAlign: "center", minWidth: 0 }}>
            <div style={supporting}>{groceryWeekLabel(week, thisWeek)}</div>
            <div style={{ fontWeight: 700, fontSize: FONT_SIZE.lg }}>{weekRange(week)}</div>
          </div>
          <button type="button" aria-label="Next week" onClick={() => goToWeek(shiftWeek(week, 1))} style={roundButton}>
            ›
          </button>
        </div>
        {week !== thisWeek && (
          <button type="button" onClick={() => goToWeek(thisWeek)} style={{ ...textButton, alignSelf: "center", fontSize: FONT_SIZE.sm }}>
            Back to this week
          </button>
        )}

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: SPACE.md, fontSize: FONT_SIZE.sm }}>
            <strong aria-live="polite">{counts.total === 0 ? "Nothing to buy yet" : done ? `All ${counts.total} done` : `${counts.left} of ${counts.total} left`}</strong>
            <span style={{ color: grocery.online && !grocery.saveFailed ? COLORS.soft : COLORS.caution, fontWeight: grocery.online && !grocery.saveFailed ? 400 : 700 }}>{sync}</span>
          </div>
          {counts.total > 0 && (
            <div aria-hidden="true" style={{ height: 8, borderRadius: RADIUS.pill, background: COLORS.line, marginTop: SPACE.sm, overflow: "hidden" }}>
              <div style={{ width: `${Math.round((counts.done / counts.total) * 100)}%`, height: "100%", background: COLORS.accent }} />
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: SPACE.md, alignItems: "stretch" }}>
          <div role="group" aria-label="List mode" style={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", border: `1px solid ${COLORS.accent}`, borderRadius: RADIUS.md, overflow: "hidden" }}>
            {(
              [
                ["shop", "Shop"],
                ["kitchen", "Check the kitchen"],
              ] as const
            ).map(([m, label]) => (
              <button
                key={m}
                type="button"
                aria-pressed={mode === m}
                onClick={() => setMode(m)}
                style={{ minHeight: LAYOUT.tapTarget, border: "none", background: mode === m ? COLORS.accent : "transparent", color: mode === m ? COLORS.onAccent : COLORS.accent, fontSize: FONT_SIZE.base, fontWeight: 700, cursor: "pointer" }}
              >
                {label}
              </button>
            ))}
          </div>
          {!isMobile && (
            <button type="button" onClick={() => window.print()} style={{ ...textButton, padding: "0 var(--space-md)" }}>
              Print
            </button>
          )}
        </div>
        {mode === "kitchen" && <p style={{ ...supporting, margin: 0 }}>Tap what you already have at home. It moves to "Already have" and isn't counted as left to buy.</p>}

        <AddItem onAdd={grocery.addExtra} online={grocery.online} />
      </header>

      {view.recipeLineCount === 0 && (
        <div style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.xl, background: COLORS.card, padding: "var(--space-xl) var(--space-xxl)", maxWidth: 560 }}>
          <div style={{ fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h3, fontWeight: 600 }}>No meals planned for this week</div>
          <p style={{ ...supporting, margin: "var(--space-xs) 0 var(--space-md)" }}>Plan a few meals and their ingredients appear here, combined and sorted by aisle.</p>
          <button
            type="button"
            onClick={() => {
              mp.setWeekStart(week);
              navigate("/plan");
            }}
            style={{ ...textButton }}
          >
            Plan this week ›
          </button>
        </div>
      )}

      {done && (
        <div style={{ border: `1px solid ${COLORS.accent}`, borderRadius: RADIUS.xl, background: COLORS.accentTint, padding: "var(--space-xl) var(--space-xxl)", maxWidth: 560 }}>
          <div style={{ fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h3, fontWeight: 600 }}>You have everything for this week</div>
          <p style={{ ...supporting, margin: "var(--space-xs) 0 var(--space-md)" }}>
            {boughtCount} bought, {haveCount} already at home.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: SPACE.x3 }}>
            <Link to="/home" style={textButton}>
              Back to Home
            </Link>
            <button type="button" onClick={() => goToWeek(shiftWeek(week, 1))} style={textButton}>
              Next week&apos;s list ›
            </button>
          </div>
        </div>
      )}

      {view.aisles.length > 0 && (
        <div style={{ columnWidth: isMobile ? undefined : 280, columnGap: SPACE.x4 }}>
          {view.aisles.map((aisle) => (
            <section key={aisle.category} style={{ breakInside: "avoid", marginBottom: SPACE.x3 }}>
              <h2 style={{ ...smallLabel, color: COLORS.accent, margin: 0 }}>{aisle.label}</h2>
              {rowList(aisle.rows, false)}
            </section>
          ))}
        </div>
      )}

      {(view.extras.length > 0 || view.extraCount > 0) && (
        <section style={{ maxWidth: 560 }}>
          <h2 style={{ ...smallLabel, color: COLORS.accent, margin: 0 }}>Added by you</h2>
          {rowList(view.extras, false)}
          <p style={{ ...supporting, fontSize: FONT_SIZE.xs, margin: "var(--space-sm) 0 0" }}>A pinned item comes back unchecked every week until you unpin it.</p>
        </section>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: SPACE.md, maxWidth: 560 }}>
        <Folded label="Already have" rows={view.have} open={open.have} onToggle={() => setOpen((o) => ({ ...o, have: !o.have }))}>
          {rowList(view.have, true)}
        </Folded>
        <Folded label="In the cart" rows={view.cart} open={open.cart} onToggle={() => setOpen((o) => ({ ...o, cart: !o.cart }))}>
          {rowList(view.cart, true)}
        </Folded>
        <Folded label="Not buying this week" rows={view.removed} open={open.removed} onToggle={() => setOpen((o) => ({ ...o, removed: !o.removed }))}>
          {view.removed.map((row) => (
            <div key={rowKey(row)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE.md, minHeight: LAYOUT.tapTarget, borderBottom: `1px solid ${COLORS.line}` }}>
              <span style={{ color: COLORS.soft }}>{capitalizeFirst(rowName(row))}</span>
              <button type="button" onClick={() => setRowMark(row, null)} style={{ ...textButton, fontSize: FONT_SIZE.sm }}>
                Put back
              </button>
            </div>
          ))}
        </Folded>
      </div>

      {isMobile && counts.total > 0 && (
        <button type="button" onClick={() => window.print()} style={{ ...textButton, alignSelf: "flex-start" }}>
          Print the list
        </button>
      )}

      <PrintPortal>
        <div style={{ fontSize: FONT_SIZE.xl, fontWeight: 700 }}>{groceryWeekLabel(week, thisWeek)}</div>
        <div style={{ fontSize: FONT_SIZE.sm, marginBottom: SPACE.xxl }}>
          {weekRange(week)} · {counts.left} to buy
        </div>
        {view.aisles.map((aisle) => (
          <div key={aisle.category} style={{ marginBottom: SPACE.xl }}>
            <div style={{ fontSize: FONT_SIZE.base, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{aisle.label}</div>
            {aisle.rows.map((row) => (
              <div key={rowKey(row)} style={{ fontSize: FONT_SIZE.base }}>
                ☐ {capitalizeFirst(rowName(row))}
                {rowAmount(row) ? ` · ${rowAmount(row)}` : ""}
              </div>
            ))}
          </div>
        ))}
        {view.extras.length > 0 && (
          <div>
            <div style={{ fontSize: FONT_SIZE.base, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Added by you</div>
            {view.extras.map((row) => (
              <div key={rowKey(row)} style={{ fontSize: FONT_SIZE.base }}>
                ☐ {capitalizeFirst(rowName(row))}
                {rowAmount(row) ? ` · ${rowAmount(row)}` : ""}
              </div>
            ))}
          </div>
        )}
      </PrintPortal>
    </div>
  );
}

// One line of the list. The main part toggles the mark; beside it, a
// recipe line's "2 recipes" opens where it's used (and "Not buying this
// week"), an added item's Pin / Pinned and ×.
function GroceryRow({
  row,
  checked,
  open,
  sources,
  onToggle,
  onDetail,
  onRemove,
  onPin,
  onDelete,
}: {
  row: Row;
  checked: boolean;
  open: boolean;
  sources: { title: string; where: string }[];
  onToggle: () => void;
  onDetail: () => void;
  onRemove: () => void;
  onPin: (pinned: boolean) => void;
  onDelete: () => void;
}) {
  const name = capitalizeFirst(rowName(row));
  const amount = rowAmount(row);
  const pinned = row.kind === "extra" && row.extra.lastWeek === null;
  return (
    <div style={{ borderBottom: `1px solid ${COLORS.line}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: SPACE.sm, minHeight: 52 }}>
        <button
          type="button"
          role="checkbox"
          aria-checked={checked}
          onClick={onToggle}
          style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: SPACE.lg, minHeight: 52, padding: "var(--space-sm) 0", border: "none", background: "transparent", color: checked ? COLORS.soft : COLORS.ink, textAlign: "left", cursor: "pointer", fontSize: FONT_SIZE.base }}
        >
          <span
            aria-hidden="true"
            style={{ width: 24, height: 24, flexShrink: 0, borderRadius: "var(--space-sm)", border: `2px solid ${checked ? COLORS.accent : COLORS.soft}`, background: checked ? COLORS.accent : "transparent", color: COLORS.onAccent, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: FONT_SIZE.sm, fontWeight: 700 }}
          >
            {checked ? "✓" : ""}
          </span>
          <span style={{ minWidth: 0 }}>
            <span style={{ textDecoration: checked ? "line-through" : "none" }}>{name}</span>
            {amount && <span style={{ ...supporting, display: "block" }}>{amount}</span>}
          </span>
        </button>
        {row.kind === "line" ? (
          <button type="button" aria-expanded={open} aria-label={`Which recipes need ${name.toLowerCase()}`} onClick={onDetail} style={{ ...textButton, color: COLORS.soft, fontWeight: 400, fontSize: FONT_SIZE.sm, flexShrink: 0 }}>
            {row.line.recipeIds.length} recipe{row.line.recipeIds.length === 1 ? "" : "s"}
          </button>
        ) : (
          <>
            <button
              type="button"
              aria-pressed={pinned}
              onClick={() => onPin(!pinned)}
              style={{ minHeight: 36, borderRadius: RADIUS.pill, padding: "0 var(--space-lg)", border: `1px solid ${pinned ? COLORS.accent : COLORS.line}`, background: pinned ? COLORS.accentTint : "transparent", color: pinned ? COLORS.accent : COLORS.soft, fontSize: FONT_SIZE.sm, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}
            >
              {pinned ? "Pinned" : "Pin"}
            </button>
            <button type="button" aria-label={`Delete ${name}`} onClick={onDelete} style={{ width: LAYOUT.tapTarget, height: LAYOUT.tapTarget, border: "none", background: "transparent", color: COLORS.danger, fontSize: FONT_SIZE.h4, cursor: "pointer", flexShrink: 0 }}>
              ×
            </button>
          </>
        )}
      </div>
      {open && row.kind === "line" && (
        <div style={{ padding: "0 0 var(--space-md) var(--space-x4)", display: "flex", flexDirection: "column", gap: SPACE.xs }}>
          {sources.map((s) => (
            <div key={s.title} style={{ fontSize: FONT_SIZE.sm }}>
              <strong>{s.title}</strong>
              {s.where && <span style={{ color: COLORS.soft }}> · {s.where}</span>}
            </div>
          ))}
          {!checked && (
            <button type="button" onClick={onRemove} style={{ ...textButton, alignSelf: "flex-start", color: COLORS.danger, fontSize: FONT_SIZE.sm }}>
              Not buying this week
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Folded({ label, rows, open, onToggle, children }: { label: string; rows: Row[]; open: boolean; onToggle: () => void; children: ReactNode }) {
  if (rows.length === 0) return null;
  return (
    <section>
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        style={{ width: "100%", minHeight: 48, display: "flex", justifyContent: "space-between", alignItems: "center", border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, background: COLORS.card, padding: "0 var(--space-xl)", color: COLORS.ink, fontSize: FONT_SIZE.base, fontWeight: 700, cursor: "pointer" }}
      >
        <span>
          {label} ({rows.length})
        </span>
        <span aria-hidden="true">{open ? "▴" : "▾"}</span>
      </button>
      {open && <div style={{ padding: "0 var(--space-xs)" }}>{children}</div>}
    </section>
  );
}

// "Add an item": a name and an optional amount. Added items need a
// connection (they aren't queued like checks).
function AddItem({ onAdd, online }: { onAdd: (name: string, amount?: string) => Promise<void>; online: boolean }) {
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await onAdd(name, amount);
      setName("");
      setAmount("");
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: SPACE.xs }}>
      <label htmlFor="add-item" style={supporting}>
        Add an item
      </label>
      <div style={{ display: "flex", gap: SPACE.md, flexWrap: "wrap" }}>
        <input id="add-item" value={name} onChange={(e) => setName(e.target.value)} placeholder="Milk, coffee, dish soap" disabled={!online} style={{ ...inputStyle, flex: "2 1 160px", minWidth: 0 }} />
        <input aria-label="Amount (optional)" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" disabled={!online} style={{ ...inputStyle, flex: "1 1 90px", minWidth: 0 }} />
        <button type="submit" disabled={!online || !name.trim() || busy} style={{ minHeight: LAYOUT.tapTarget, border: `1px solid ${COLORS.accent}`, borderRadius: RADIUS.md, padding: "0 var(--space-xxl)", background: "transparent", color: COLORS.accent, fontSize: FONT_SIZE.base, fontWeight: 700, cursor: "pointer" }}>
          Add
        </button>
      </div>
      {!online && <span style={{ ...supporting, color: COLORS.caution }}>Adding items needs a connection.</span>}
      {failed && <span style={{ ...supporting, color: COLORS.danger }}>That didn&apos;t save. Try again.</span>}
    </form>
  );
}
